// ══════════════════════════════════════════════════════════════════════
// Ask GQ™ AI Chat — workflow-aware + tool-calling
// • Receives GQ.state.serialize() context every turn
// • AI can issue <action ... /> tags that we parse and execute
// • Per-thread persistent history (LocalStorage + Supabase mirror)
// ══════════════════════════════════════════════════════════════════════

// Tool registry now lives in lib/ai-tools.js — accessed via window.GQ.aiTools.
// Legacy GQ_AI_TOOLS export kept as a thin shim so existing callers (and
// the legacy explainMetric inline cards) keep working.
function _toolGet(name) { return window.GQ?.aiTools?.get?.(name); }
const GQ_AI_TOOLS = new Proxy({}, {
  get(_t, name) {
    const tool = _toolGet(name);
    return tool ? (args, onProgress) => tool.execute(args, onProgress) : undefined;
  },
});

// Parse <action name="..." k="v" /> tags from model output
function parseActions(text) {
  const re = /<action\s+name="([^"]+)"([^/]*?)\/?>/g;
  const actions = []; let m;
  while ((m = re.exec(text)) !== null) {
    const attrs = {};
    const attrRe = /(\w+)="([^"]*)"/g; let a;
    while ((a = attrRe.exec(m[2])) !== null) attrs[a[1]] = a[2];
    actions.push({ name: m[1], args: attrs });
  }
  return actions;
}

function buildSystemPrompt(stateCtx, page) {
  const ctx = stateCtx || {};
  const strat = ctx.currentStrategy
    ? `Current strategy: "${ctx.currentStrategy.name}" (${ctx.currentStrategy.market || 'mixed'}, ${ctx.currentStrategy.strategy_type || 'unspecified'})`
    : 'No active strategy.';
  const lastBT = ctx.lastBacktest
    ? `Last backtest: ${ctx.lastBacktest.id.slice(0, 8)} — Sharpe ${ctx.lastBacktest.metrics?.sharpe?.toFixed(2) || '?'}, MaxDD ${(ctx.lastBacktest.metrics?.maxDrawdown * 100)?.toFixed(1) || '?'}%`
    : 'No backtest yet.';
  const lastOpt = ctx.lastOptimization
    ? `Last optimization: ${ctx.lastOptimization.id.slice(0, 8)} — best Sharpe ${ctx.lastOptimization.metrics?.bestFitness?.toFixed?.(2) || '?'}`
    : 'No optimization yet.';
  const lastStress = ctx.lastStress
    ? `Last stress run: ${ctx.lastStress.id.slice(0, 8)} — composite score ${ctx.lastStress.metrics?.score || '?'}/100`
    : 'No stress run yet.';

  return `You are GQ™ AI, an institutional-grade quant assistant inside the Genetic Quant.ai platform.

PIPELINE CONTEXT:
- Current page: ${page}
- ${strat}
- ${lastBT}
- ${lastOpt}
- ${lastStress}

WORKFLOW: Mint → Strategy Lab → Backtest → Optimize → Stress Test → Deploy
The user moves through this pipeline. Reference the linked runs when answering.

AVAILABLE ACTIONS (emit as XML tags, one per line, anywhere in your reply):
${window.GQ?.aiTools?.promptHelp?.() || '  (registry unavailable)'}

ACTION RULES:
- Engine-running actions (runBacktestNow / runOptimizationNow / runStressNow) require user approval; emit them but expect a pause for confirmation.
- Read-only tools (fetchRun / listStrategies / explainMetric / navigate / setCurrentStrategy) run instantly.
- Don't emit the same action twice in one reply.
- If the user asks "do X", emit an action — don't just describe what you would do.

STYLE: Concise (under 150 words), professional, quant-fluent. Reference specific numbers from the context. Avoid generic disclaimers.`;
}

// ──────────────────────────────────────────────────────────────────────
// Persistent chat history per thread
// ──────────────────────────────────────────────────────────────────────
async function loadThread(threadKey) {
  // LocalStorage first
  const lsKey = 'gq:ai:' + threadKey;
  try {
    const raw = localStorage.getItem(lsKey);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  // Supabase fallback
  if (window.sbClient) {
    try {
      const { data: { user } } = await window.sbClient.auth.getUser();
      if (user) {
        const { data } = await window.sbClient
          .from('ai_conversations')
          .select('messages')
          .eq('user_id', user.id)
          .eq('thread_key', threadKey)
          .maybeSingle();
        if (data?.messages) return data.messages;
      }
    } catch (e) {}
  }
  return null;
}
async function saveThread(threadKey, messages) {
  const lsKey = 'gq:ai:' + threadKey;
  try { localStorage.setItem(lsKey, JSON.stringify(messages.slice(-50))); } catch (e) {}
  if (window.sbClient) {
    try {
      const { data: { user } } = await window.sbClient.auth.getUser();
      if (!user) return;
      const strategyId = window.GQ?.state?.getCurrentStrategyId() || null;
      await window.sbClient.from('ai_conversations').upsert({
        user_id: user.id,
        strategy_id: strategyId,
        thread_key: threadKey,
        messages: messages.slice(-50),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,thread_key' });
    } catch (e) {}
  }
}

// ──────────────────────────────────────────────────────────────────────
// Main panel
// ──────────────────────────────────────────────────────────────────────
function AskGQChat({ open, onClose, page, route }) {
  const threadKey = React.useMemo(() => {
    const s = window.GQ?.state?.getCurrentStrategyId();
    return s ? `strategy:${s}` : 'global';
  }, [open]);

  const [messages, setMessages] = React.useState([
    { role: "assistant", content: "Hi — I'm GQ™ AI. I can run backtests, optimizations and stress tests for you. Try: \"backtest my newest strategy on SPY 2024\" or \"optimize Sharpe with Bayesian search\". Every engine-running action needs your approval first." }
  ]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [actionLog, setActionLog] = React.useState([]);
  const scrollRef = React.useRef(null);

  // Hydrate history when opening
  React.useEffect(() => {
    if (!open) return;
    loadThread(threadKey).then(hist => {
      if (hist && hist.length) setMessages(hist);
    });
  }, [open, threadKey]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const PROMPTS = {
    vault:       ["Summarise my pipeline state", "Which strategy should I optimize next?", "What's my worst risk concentration?"],
    tearsheet:   ["Explain my Sharpe ratio", "Diagnose the drawdown episode", "Compare to benchmark"],
    designer:    ["Build me a momentum strategy", "Add a volatility filter", "Suggest indicator improvements"],
    backtest:    ["Explain these results", "What's overfitted here?", "Suggest a walk-forward split"],
    optimize:    ["Why is convergence slow?", "Open the Pareto frontier", "Run NSGA-II on Sharpe vs DD"],
    stress:      ["Which scenario is most worrying?", "Run a black-swan test", "Explain my VaR vs CVaR"],
    marketplace: ["Which strategy fits my risk profile?", "What's the best Sharpe in the market?", "Compare top 3 by Calmar"],
    settings:    ["How do I connect an exchange?", "What does API access give me?"],
    admin:       ["Show pending requests", "Audit recent access changes"],
    default:     ["What can you do?", "Show me the pipeline status", "Help me get started"],
  };
  const suggestions = PROMPTS[route] || PROMPTS.default;

  // Execute an action message in-place, streaming progress into its `progress`
  // field. Used by both auto-execute (confirm:false) and approve-button.
  async function executeAction(idx) {
    const messagesNow = (typeof idx === 'object') ? idx.snapshot : messages;
    const actualIdx   = (typeof idx === 'object') ? idx.idx     : idx;
    const msg = messagesNow[actualIdx];
    if (!msg || msg.role !== 'action') return;
    const tool = window.GQ?.aiTools?.get?.(msg.actionName);
    if (!tool) {
      updateAction(actualIdx, { status: 'failed', result: `Unknown tool: ${msg.actionName}` });
      return;
    }
    // Rate limit
    if (window.GQ?.aiTools?.rateRemaining?.() <= 0) {
      updateAction(actualIdx, { status: 'failed', result: 'Rate limit reached (20/hour). Try again later.' });
      return;
    }
    updateAction(actualIdx, { status: 'running', progress: 'starting…' });
    try {
      const onProgress = (p) => updateAction(actualIdx, { progress: p });
      const result = await tool.execute(msg.args, onProgress);
      window.GQ.aiTools.recordExec();
      updateAction(actualIdx, { status: 'done', result, progress: null });
      window.gqTrack?.('ai_tool_execute', { tool: msg.actionName });
      window.dispatchEvent(new CustomEvent('gq:tool-executed', { detail: { name: msg.actionName, result } }));
    } catch (e) {
      updateAction(actualIdx, { status: 'failed', result: e.message, progress: null });
    }
  }

  function updateAction(idx, patch) {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, ...patch } : m));
  }

  function rejectAction(idx) {
    updateAction(idx, { status: 'rejected', result: 'Rejected by user.' });
  }

  async function send(text) {
    if (!text.trim() || busy) return;
    const userMsg = { role: "user", content: text, ts: Date.now(), page };
    const next1 = [...messages, userMsg];
    setMessages(next1); setInput(""); setBusy(true);

    // Rebuild context FRESH on every send so the AI sees runs/strategies
    // started in other tabs while the chat panel was open.
    const freshCtx = window.GQ?.state?.serialize?.() || {};
    const sys = buildSystemPrompt(freshCtx, page);
    const recentHistory = next1.slice(-12)
      .map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n\n');
    const fullPrompt = `${sys}\n\nCONVERSATION:\n${recentHistory}\n\nASSISTANT:`;

    // Timeout guard — 60 s max for any single AI call
    let response = '';
    try {
      response = await Promise.race([
        window.claude.complete(fullPrompt),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 60000)),
      ]);
    } catch (e) {
      response = e.message === 'timeout'
        ? "AI service timed out. Try a shorter request."
        : e.message?.includes('429') || e.message?.includes('rate_limit')
          ? "Rate limit reached — you've hit the hourly cap. Try again in a few minutes."
          : "Couldn't reach the AI service. Try again in a moment.";
    }

    // Parse action tags; assistant text strips them
    const actions = parseActions(response);
    const displayText = response.replace(/<action[^>]*\/?>/g, '').trim();
    const assistantMsg = { role: "assistant", content: displayText, ts: Date.now() };

    // Build action messages — confirm:false auto-run, confirm:true wait
    const actionMsgs = actions.map(a => {
      const tool = window.GQ?.aiTools?.get?.(a.name);
      const needsConfirm = !!tool?.confirm;
      return {
        role: 'action',
        actionName: a.name,
        args: a.args,
        status: !tool ? 'failed' : needsConfirm ? 'awaiting' : 'running',
        result: !tool ? `Unknown tool: ${a.name}` : null,
        progress: !tool ? null : needsConfirm ? null : 'starting…',
        ts: Date.now(),
      };
    });

    const next2 = [...next1, assistantMsg, ...actionMsgs];
    setMessages(next2);
    setBusy(false);
    saveThread(threadKey, next2);

    // Auto-execute confirm:false actions
    for (let i = 0; i < actionMsgs.length; i++) {
      const am = actionMsgs[i];
      if (am.status !== 'running') continue;
      const idxInFull = next2.length - actionMsgs.length + i;
      executeAction({ snapshot: next2, idx: idxInFull });
    }
  }

  return (
    <>
      {open && <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", backdropFilter:"blur(2px)", zIndex:5000 }}/>}
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, width:"min(440px,100vw)",
        background:"var(--bg-card)", borderLeft:"1px solid var(--border-strong)",
        zIndex:5001, transform: open ? "translateX(0)" : "translateX(100%)",
        transition:"transform .25s cubic-bezier(0.4,0,0.2,1)",
        display:"flex", flexDirection:"column",
        boxShadow: open ? "-12px 0 40px rgba(0,0,0,0.4)" : "none",
      }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:"rgba(217,70,239,0.12)", border:"1px solid rgba(217,70,239,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ fontFamily:"'Inter Tight', 'Inter', sans-serif", fontSize:13, color:"#D946EF", fontWeight:800, letterSpacing:"-0.02em" }}>GQ</span>
            </div>
            <div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)" }}>Ask GQ™</div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>
                {page} · {(window.GQ?.aiTools?.rateRemaining?.() ?? '–')}/{window.GQ?.aiTools?.rateLimit?.() ?? 20} actions left this hour
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", padding:4 }}><X size={16}/></button>
        </div>

        <div ref={scrollRef} style={{ flex:1, overflowY:"auto", padding:18, display:"flex", flexDirection:"column", gap:14 }}>
          {messages.map((m, i) => {
            // Action card: pending / running / done / failed / rejected
            if (m.role === 'action') {
              const colors = {
                awaiting: { bg: "rgba(217,70,239,0.08)", bd: "rgba(217,70,239,0.30)", fg: "#D946EF" },
                running:  { bg: "rgba(59,158,255,0.08)", bd: "rgba(59,158,255,0.30)", fg: "#3b9eff" },
                done:     { bg: "rgba(17,255,153,0.08)", bd: "rgba(17,255,153,0.30)", fg: "#11ff99" },
                failed:   { bg: "rgba(255,32,71,0.08)",  bd: "rgba(255,32,71,0.30)",  fg: "#ff2047" },
                rejected: { bg: "rgba(255,255,255,0.03)", bd: "var(--border)",          fg: "var(--muted)" },
              };
              const c = colors[m.status] || colors.awaiting;
              const argSummary = Object.entries(m.args || {}).map(([k,v]) => `${k}=${v}`).join(' · ');
              return (
                <div key={i} style={{
                  alignSelf:"stretch", maxWidth:"100%",
                  background: c.bg, border: `1px solid ${c.bd}`, borderRadius:10,
                  padding:"10px 12px",
                }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                      <span style={{
                        width:6, height:6, borderRadius:"50%", background: c.fg,
                        ...(m.status === 'running' ? { animation:"pulse 1.2s ease-in-out infinite" } : {}),
                      }}/>
                      <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color: c.fg, fontWeight:600 }}>
                        {m.actionName}
                      </span>
                      <span style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".05em" }}>
                        {m.status}
                      </span>
                    </div>
                    {m.status === 'awaiting' && (
                      <div style={{ display:"flex", gap:5 }}>
                        <button className="btn btn-ghost btn-sm" style={{padding:"3px 8px",fontSize:10}} onClick={() => rejectAction(i)}>Reject</button>
                        <button className="btn btn-primary btn-sm" style={{padding:"3px 10px",fontSize:10}} onClick={() => executeAction(i)}>Approve</button>
                      </div>
                    )}
                  </div>
                  {argSummary && (
                    <div style={{ marginTop:6, fontFamily:"var(--font-mono)", fontSize:10, color:"var(--muted)" }}>
                      {argSummary}
                    </div>
                  )}
                  {m.progress && (
                    <div style={{ marginTop:6, fontFamily:"var(--font-ui)", fontSize:11, color: c.fg, fontStyle:"italic" }}>
                      {m.progress}
                    </div>
                  )}
                  {m.result && (m.status === 'done' || m.status === 'failed' || m.status === 'rejected') && (
                    <div style={{ marginTop:6, fontFamily:"var(--font-ui)", fontSize:12, color:"var(--ink)", lineHeight:1.5, whiteSpace:"pre-wrap" }}>
                      {String(m.result)}
                    </div>
                  )}
                </div>
              );
            }
            // Normal user / assistant message
            return (
              <div key={i} style={{
                alignSelf: m.role==="user" ? "flex-end" : "flex-start",
                maxWidth:"85%",
                background: m.role==="user" ? "rgba(17,255,153,0.10)" : "rgba(255,255,255,0.04)",
                border: m.role==="user" ? "1px solid rgba(17,255,153,0.2)" : "1px solid var(--border)",
                borderRadius: m.role==="user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                padding:"10px 14px",
                fontFamily:"var(--font-ui)", fontSize:13, color:"var(--ink)", lineHeight:1.55,
                whiteSpace:"pre-wrap",
              }}>
                {m.content}
              </div>
            );
          })}
          {busy && (
            <div style={{ alignSelf:"flex-start", display:"flex", gap:5, padding:"10px 14px" }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--muted)", animation:"pulse 1.2s ease-in-out infinite" }}/>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--muted)", animation:"pulse 1.2s ease-in-out .2s infinite" }}/>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--muted)", animation:"pulse 1.2s ease-in-out .4s infinite" }}/>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div style={{ padding:"0 18px 12px", display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:4 }}>Suggested</div>
            {suggestions.map((s,i) => (
              <button key={i} onClick={() => send(s)} style={{
                background:"transparent", border:"1px solid var(--border)", borderRadius:"var(--r-md)",
                padding:"7px 10px", cursor:"pointer", textAlign:"left",
                fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)",
              }}>{s}</button>
            ))}
          </div>
        )}

        <div style={{ padding:14, borderTop:"1px solid var(--border)", display:"flex", gap:8 }}>
          <input
            value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter") send(input); }}
            placeholder="Ask GQ™ anything…"
            disabled={busy}
            className="input"
            style={{ flex:1 }}
          />
          <button onClick={()=>send(input)} disabled={busy || !input.trim()} style={{
            display:"inline-flex", alignItems:"center", gap:6,
            background:"#fcfdff", color:"#D946EF", border:"none",
            borderRadius:"var(--r-full)", padding:"5px 14px", cursor:"pointer",
            fontFamily:"var(--font-ui)", fontSize:12, fontWeight:600,
            opacity: (busy||!input.trim())?0.5:1,
            transition:"opacity .15s",
          }}>
            Send
          </button>
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:1} }`}</style>
    </>
  );
}

function AskGQChip({ onClick }) {
  return (
    <button onClick={onClick} style={{
      display:"inline-flex", alignItems:"center", gap:6,
      background:"rgba(17,255,153,0.08)", border:"1px solid rgba(17,255,153,0.25)",
      borderRadius:"var(--r-full)", padding:"4px 10px", cursor:"pointer",
      fontFamily:"var(--font-ui)", fontSize:12, color:"#11ff99", fontWeight:500,
    }}>
      <span style={{ fontFamily:"'Inter Tight', 'Inter', sans-serif", fontWeight:800, letterSpacing:"-0.02em" }}>GQ</span>
      Ask AI
    </button>
  );
}

// Pipeline context chip — shows which run is currently loaded per page
function PipelineContextChip({ page }) {
  const [snap, setSnap] = React.useState(() =>
    window.GQ?.state?.snapshot ? window.GQ.state.snapshot() : null);
  React.useEffect(() => {
    if (!window.GQ?.state) return;
    return window.GQ.state.subscribe((s) => setSnap({ ...s }));
  }, []);
  if (!snap) return null;
  const map = {
    backtest: { id: snap.lastBacktestRunId,     label: 'Backtest' },
    optimize: { id: snap.lastOptimizationRunId, label: 'Optimize' },
    stress:   { id: snap.lastStressRunId,       label: 'Stress' },
  };
  const here = map[page];
  if (!here || !here.id) return null;
  return (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:6,
      background:"rgba(217,70,239,0.08)", border:"1px solid rgba(217,70,239,0.25)",
      borderRadius:"var(--r-full)", padding:"3px 10px",
      fontFamily:"var(--font-mono)", fontSize:11, color:"#D946EF",
    }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:"#D946EF" }}></span>
      {here.label} {here.id.slice(0, 8)}
    </div>
  );
}

window.AskGQChat = AskGQChat;
window.AskGQChip = AskGQChip;
window.PipelineContextChip = PipelineContextChip;
window.GQ_AI_TOOLS = GQ_AI_TOOLS;
